import { type FC, type PropsWithChildren } from 'react';
import { useAppShell } from 'src/components/layout/app-shell/use-app-shell-provider';
import { ExternalLink } from 'src/components/navigation/link';
import { useNow } from 'src/hooks/use-now';
import { cn } from 'src/utils/cn';

const separatorClassNames = cn(`
  border-border-surface-minimal
  not-last:border-r not-last:pr-lg
`);

const linkClassNames = cn(`
  text-body-sm! text-foreground-surface-subdued
  hover:text-foreground-surface-default
`);

const FooterLink: FC<
  PropsWithChildren<{
    href: string;
    target?: string;
  }>
> = ({ href, target, children }) => {
  return (
    <li className={cn(separatorClassNames)}>
      <ExternalLink href={href} target={target} className={cn(linkClassNames)}>
        {children}
      </ExternalLink>
    </li>
  );
};

export const Footer: FC = () => {
  const { registerForInfiniteScrolling, infiniteScrollingMode } = useAppShell();
  const now = useNow();
  return (
    <div
      className={cn(
        `
          border-t border-border-surface-minimal bg-background-default
          px-[calc(var(--spacing-xl)+var(--spacing-lg))] py-xl text-body-sm
          text-foreground-surface-subdued
          md:px-3xl
          print:hidden
        `,
        registerForInfiniteScrolling &&
          'sticky bottom-0 w-full transition-all duration-300 ease-in-out',
        registerForInfiniteScrolling
          ? infiniteScrollingMode === 'after-threshold' ||
            infiniteScrollingMode === 'default'
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-5xl opacity-0'
          : null
      )}
      data-testid="footer"
    >
      <ul className="flex flex-wrap gap-lg">
        <FooterLink href="https://www.prisma-capacity.eu/" target="_blank">
          Corporate Website
        </FooterLink>
        <FooterLink href="https://help.prisma-capacity.eu/" target="_blank">
          Support Portal
        </FooterLink>
        <FooterLink
          href="https://www.prisma-capacity.eu/newsletter"
          target="_blank"
        >
          Join The Newsletter
        </FooterLink>
        <FooterLink
          href="https://www.prisma-capacity.eu/privacy"
          target="_blank"
        >
          Privacy Policy
        </FooterLink>
        <FooterLink
          href="https://www.prisma-capacity.eu/platform-usage-contract-puc"
          target="_blank"
        >
          Platform Usage Conditions
        </FooterLink>
        <FooterLink
          href="https://www.prisma-capacity.eu/downloads"
          target="_blank"
        >
          Downloads
        </FooterLink>
        <FooterLink
          href="https://www.prisma-capacity.eu/impressum"
          target="_blank"
        >
          Imprint
        </FooterLink>
        <li className={cn(separatorClassNames)}>
          <span className="text-foreground-surface-disabled">
            Copyright @ PRISMA {new Date(now).getFullYear()}
          </span>
        </li>
      </ul>
    </div>
  );
};
